import * as Sentry from '@sentry/react'

import { PinturaNode, PinturaNodeOptions, findNode } from '@pqina/pintura'

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import AutoFixNormalOutlinedIcon from '@mui/icons-material/AutoFixNormalOutlined'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import BlurOnOutlinedIcon from '@mui/icons-material/BlurOnOutlined'
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined'
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined'
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined'
import CropFreeOutlinedIcon from '@mui/icons-material/CropFreeOutlined'
import CropOutlinedIcon from '@mui/icons-material/CropOutlined'
import FilterOutlinedIcon from '@mui/icons-material/FilterOutlined'
import FormatColorFillOutlinedIcon from '@mui/icons-material/FormatColorFillOutlined'
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined'
import { GRAY_800 } from 'constants/styling/theme'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import ZoomOutMapOutlinedIcon from '@mui/icons-material/ZoomOutMapOutlined'

export enum DEPRECATED_AI_TOOLS {
  AI_MAGIC_ERASER = 'AI_MAGIC_ERASER',
  AI_AUTO_ENHANCEMENT = 'AI_AUTO_ENHANCEMENT',
  AI_SKY_REPLACEMENT = 'AI_SKY_REPLACEMENT',
  AI_WALL_PAINTING = 'AI_WALL_PAINTING',
}

export enum AI_TOOLS {
  MAGIC_ERASER = 'MAGIC_ERASER',
  AUTO_ENHANCEMENT = 'AUTO_ENHANCEMENT',
  VIRTUAL_STAGING = 'VIRTUAL_STAGING',
  VISUAL_STAGING = 'VISUAL_STAGING',
}

export enum DEPRECATED_MANUAL_TOOLS {
  CROP = 'CROP',
  FINE_TUNE = 'FINE_TUNE',
  FILTERS = 'FILTERS',
  ANNOTATE = 'ANNOTATE',
  STICKER = 'STICKER',
  FILL = 'FILL',
  REDACT = 'REDACT',
  FRAME = 'FRAME',
  RESIZE = 'RESIZE',
}

export enum MANUAL_EDIT_TOOLS {
  FINE_TUNE = 'FINE_TUNE',
  CROP = 'CROP',
  RESIZE = 'RESIZE',
  BLUR = 'BLUR',
  ANNOTATE = 'ANNOTATE',
  STICKER = 'STICKER',
}

const _deprecated_comingSoonTools = [DEPRECATED_AI_TOOLS.AI_WALL_PAINTING, DEPRECATED_AI_TOOLS.AI_SKY_REPLACEMENT, DEPRECATED_AI_TOOLS.AI_AUTO_ENHANCEMENT]

export const getAIToolIcon = (tool: string) => {
  switch (tool) {
    case DEPRECATED_AI_TOOLS.AI_AUTO_ENHANCEMENT:
      return <AutoAwesomeOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_AI_TOOLS.AI_MAGIC_ERASER:
      return <AutoFixNormalOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_AI_TOOLS.AI_SKY_REPLACEMENT:
      return <CloudOffOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_AI_TOOLS.AI_WALL_PAINTING:
      return <FormatPaintOutlinedIcon sx={{ color: GRAY_800 }} />
    default:
      return <AutoAwesomeOutlinedIcon sx={{ color: GRAY_800 }} />
  }
}

export const getManualToolIcon = (tool: string) => {
  switch (tool) {
    case DEPRECATED_MANUAL_TOOLS.CROP:
      return <CropOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.FINE_TUNE:
      return <TuneOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.FILTERS:
      return <FilterOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.ANNOTATE:
      return <BorderColorOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.STICKER:
      return <LabelOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.FILL:
      return <FormatColorFillOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.REDACT:
      return <BlurOnOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.FRAME:
      return <CropFreeOutlinedIcon sx={{ color: GRAY_800 }} />
    case DEPRECATED_MANUAL_TOOLS.RESIZE:
      return <ZoomOutMapOutlinedIcon sx={{ color: GRAY_800 }} />
    default:
      return <ConstructionOutlinedIcon sx={{ color: GRAY_800 }} />
  }
}

export const getManualToolIconRedesigned = (tool: string) => {
  switch (tool) {
    case MANUAL_EDIT_TOOLS.CROP:
      return <CropOutlinedIcon sx={{ color: GRAY_800 }} />
    case MANUAL_EDIT_TOOLS.FINE_TUNE:
      return <TuneOutlinedIcon sx={{ color: GRAY_800 }} />
    case MANUAL_EDIT_TOOLS.ANNOTATE:
      return <BorderColorOutlinedIcon sx={{ color: GRAY_800 }} />
    case MANUAL_EDIT_TOOLS.STICKER:
      return <LabelOutlinedIcon sx={{ color: GRAY_800 }} />
    case MANUAL_EDIT_TOOLS.RESIZE:
      return <ZoomOutMapOutlinedIcon sx={{ color: GRAY_800 }} />
    case MANUAL_EDIT_TOOLS.BLUR:
      return <BlurOnIcon sx={{ color: GRAY_800 }} />
    default:
      return <ConstructionOutlinedIcon sx={{ color: GRAY_800 }} />
  }
}

// todo remove this when removing old version of  photo studio
export const toolToPinturaFeatureMap: Record<DEPRECATED_MANUAL_TOOLS, string> = {
  [DEPRECATED_MANUAL_TOOLS.CROP]: 'crop',
  [DEPRECATED_MANUAL_TOOLS.FILTERS]: 'filter',
  [DEPRECATED_MANUAL_TOOLS.STICKER]: 'sticker',
  [DEPRECATED_MANUAL_TOOLS.FINE_TUNE]: 'finetune',
  [DEPRECATED_MANUAL_TOOLS.REDACT]: 'redact',
  [DEPRECATED_MANUAL_TOOLS.ANNOTATE]: 'annotate',
  [DEPRECATED_MANUAL_TOOLS.FILL]: 'fill',
  [DEPRECATED_MANUAL_TOOLS.FRAME]: 'frame',
  [DEPRECATED_MANUAL_TOOLS.RESIZE]: 'resize',
}

// The values are based on the Pintura features names from documentation: https://pqina.nl/pintura/docs/
export const toolToPinturaFeatureMapRedesigned: Record<MANUAL_EDIT_TOOLS, string> = {
  [MANUAL_EDIT_TOOLS.CROP]: 'crop',
  [MANUAL_EDIT_TOOLS.STICKER]: 'sticker',
  [MANUAL_EDIT_TOOLS.FINE_TUNE]: 'finetune',
  [MANUAL_EDIT_TOOLS.BLUR]: 'redact',
  [MANUAL_EDIT_TOOLS.ANNOTATE]: 'annotate',
  [MANUAL_EDIT_TOOLS.RESIZE]: 'resize',
}

export const getIsComingSoonTool = (tool: DEPRECATED_AI_TOOLS) => _deprecated_comingSoonTools.includes(tool)

const PINTURA_EXPORT_BTN_ID = 'export'
const PINTURA_REVERT_BTN_ID = 'revert'
const PINTURA_BETA_ID = 'beta'

export const CUSTOM_VISUAL_SWITCHER_ID = 'visual-type-switcher'
export const CUSTOM_UNDO_REDO_ID = 'photo-studio-undo-redo'

export const customizeToolbarButtons = (toolbar: PinturaNode[]) => {

  if (!Array.isArray(toolbar)) {
    Sentry.captureException('PinturacustomizeToolbarButtons: toolbar is not an array', {
      extra: {
        toolbar,
        toolbarType: typeof toolbar,
        isArray: Array.isArray(toolbar)
      }
    })

    return toolbar
  }

  // Find built-in undo/redo buttons
  const betaButton = findNode(PINTURA_BETA_ID, toolbar)
  const betaButtonOptions = (betaButton?.[2] as PinturaNodeOptions) ?? undefined

  if (betaButtonOptions) {
    // Hide Pintura's built-in undo/redo buttons - we use our custom undo/redo panel instead
    betaButtonOptions.hideLabel = true
    betaButtonOptions.class = ''
  }

  // Pintura doesn't support directly inserting custom components in the toolbar, so we create a blank button and later render our component into it.
  const visualSwitcherNode: PinturaNode = [
    'div',
    'visual-switcher-group',
    {
      class: 'PinturaNavGroup'
    },
    [
      [
        'Button',
        'visual-type-switcher',
        {
          label: '',
          icon: false,
          hideLabel: true,
          class: 'pintura-visual-switcher',
        }
      ]
    ]
  ]

  // Create undo/redo placeholder node
  const undoRedoNode: PinturaNode = [
    'div',
    'undo-redo-group',
    {
      class: 'PinturaNavGroup'
    },
    [
      [
        'Button',
        'photo-studio-undo-redo',
        {
          label: '',
          icon: false,
          hideLabel: true,
          class: 'pintura-undo-redo',
        }
      ]
    ]
  ]

  // Find redo/undo group (beta) and insert visual switcher and undo/redo before it
  const betaIndex = toolbar.findIndex(
    (node) => node[1] === PINTURA_BETA_ID
  )

  if (betaIndex !== -1) {
    toolbar.splice(betaIndex, 0, visualSwitcherNode, undoRedoNode)
  }

  return toolbar
}
